import { useState } from 'react';
import { Loader2, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import { useKioskFlowStore } from '../../stores/kiosk-flow-store';
import { FlowNameDialog } from '../molecules/FlowNameDialog';
import { FlowEditor } from './FlowEditor';

// Shows the selected operator's flow editor, or a Create action when it has none
export function OperatorFlowPanel() {
  const {
    selectedOperator,
    flow,
    isLoadingFlow,
    isCreatingFlow,
    flowError,
    createFlowError,
    loadFlow,
    createFlow,
    clearCreateFlowError,
  } = useKioskFlowStore();

  const [createOpen, setCreateOpen] = useState(false);

  if (!selectedOperator) return null;

  const handleCreate = async (name: string) => {
    const created = await createFlow(name);
    if (created) {
      setCreateOpen(false);
      toast.success(`Flow "${name.trim()}" created`);
    }
  };

  const handleCreateOpenChange = (open: boolean) => {
    if (!open) clearCreateFlowError();
    setCreateOpen(open);
  };

  if (isLoadingFlow) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading flow…
        </CardContent>
      </Card>
    );
  }

  if (flowError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-sm text-red-600">{flowError}</p>
          <Button variant="outline" size="sm" onClick={() => loadFlow()}>
            <RefreshCw className="mr-2 h-3 w-3" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!flow) {
    return (
      <>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="font-medium text-gray-900">{selectedOperator.name} has no kiosk flow yet</p>
            <p className="text-sm text-gray-500">Kiosks use their built-in default flow until one is published</p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create New Flow
            </Button>
          </CardContent>
        </Card>
        <FlowNameDialog
          open={createOpen}
          mode="create"
          operatorName={selectedOperator.name}
          isSaving={isCreatingFlow}
          error={createFlowError}
          onOpenChange={handleCreateOpenChange}
          onSubmit={handleCreate}
        />
      </>
    );
  }

  return <FlowEditor flow={flow} operatorName={selectedOperator.name} />;
}
